import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';

import { useAppDispatch, useAppSelector } from '../../store';
import { showError } from '../../store/slices/errorSlice';
import { ROUTES, WorkItemDetailStatus } from '../../utils/constants';

import AdditionalDetailsSection from './AdditionalDetailsSection';
import ExceptionDetailsSection from './ExceptionDetailsSection';
import ResolveTaskButton from './ResolveTaskButton';
import SopSection from './SopSection';
import {
    loadWorkItem,
    selectLoadStatus,
    selectLoadedWorkItemId,
    selectResolveStatus,
    selectWorkItem,
} from './slice';

import './index.scss';

/**
 * Work Item Detail Page — renders at `/work-items/detail?workItemId=<id>`.
 *
 * Wires the `workItemDetailSlice` lifecycle to the three detail sections
 * (Exception Details, Additional Details, SOP) and the Resolve Task button.
 *
 * Responsibilities owned by this component:
 *   - Read `workItemId` from the URL via `useSearchParams`.
 *   - Dispatch `loadWorkItem({ workItemId })` on mount and whenever
 *     `workItemId` changes.
 *   - Render a Meridian `Loader` while the initial load for the current
 *     `workItemId` is in flight.
 *   - Render the three sections + `ResolveTaskButton` once the load is
 *     fulfilled.
 *   - Render an inline "Could not load work item" panel on rejection,
 *     omitting the three sections.
 *   - Render an inline "Missing work item ID" panel when the query
 *     parameter is absent and dispatch `showError({ title: "Missing Work
 *     Item ID", ... })` — MUST NOT dispatch `loadWorkItem`.
 *   - Navigate back to `ROUTES.WORK_ITEMS` once `resolveStatus === FULFILLED`.
 *
 * Side effects that live elsewhere:
 *   - The thunk-owned `"Failed to Load Work Item"` / `"Failed to Resolve
 *     Task"` `showError` dispatches are in `slice.ts`.
 *   - The `"Unable to Determine Current User"` dispatch during the Resolve
 *     click is in `ResolveTaskButton.tsx`.
 */
const WorkItemDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const workItemId = searchParams.get('workItemId');

    const loadStatus = useAppSelector(selectLoadStatus);
    const resolveStatus = useAppSelector(selectResolveStatus);
    const workItem = useAppSelector(selectWorkItem);
    const loadedWorkItemId = useAppSelector(selectLoadedWorkItemId);

    // Mount / workItemId-change effect.
    useEffect(() => {
        if (!workItemId) {
            dispatch(
                showError({
                    title: 'Missing Work Item ID',
                    message: 'No work item ID was provided in the URL.',
                }),
            );
            return;
        }
        dispatch(loadWorkItem({ workItemId }));
    }, [dispatch, workItemId]);

    // Post-resolve navigation. Only fire on an actual loading → fulfilled
    // transition observed during this page's lifetime — guards against a
    // stale `resolveStatus === FULFILLED` left over from a prior resolve
    // bouncing the user back to /work-items right after they re-enter the
    // detail page from Start/Continue Task.
    const sawResolveLoadingRef = useRef(false);
    useEffect(() => {
        if (resolveStatus === WorkItemDetailStatus.LOADING) {
            sawResolveLoadingRef.current = true;
            return;
        }
        if (
            resolveStatus === WorkItemDetailStatus.FULFILLED &&
            sawResolveLoadingRef.current
        ) {
            sawResolveLoadingRef.current = false;
            navigate(ROUTES.WORK_ITEMS);
        }
    }, [resolveStatus, navigate]);

    // ── Render branches ────────────────────────────────────────────────────
    let content: React.ReactNode;

    if (!workItemId) {
        // Missing work item id branch.
        content = (
            <Column
                spacing="300"
                data-testid="work-item-detail-missing-id"
                className="work-item-detail-error-panel"
            >
                <Heading level={2}>Missing work item ID</Heading>
                <Text type="b200">
                    No work item ID was provided in the URL.
                </Text>
            </Column>
        );
    } else if (
        loadStatus === WorkItemDetailStatus.LOADING &&
        loadedWorkItemId !== workItemId
    ) {
        // Show the loader only while there's no cached work item for the
        // current id. A stale fulfilled state for the same id keeps
        // rendering the sections while a re-fetch is in flight.
        content = (
            <Box data-testid="work-item-detail-loader">
                <Loader />
            </Box>
        );
    } else if (loadStatus === WorkItemDetailStatus.REJECTED) {
        // Load-rejected branch.
        content = (
            <Column
                spacing="300"
                data-testid="work-item-detail-error"
                className="work-item-detail-error-panel"
            >
                <Heading level={2}>Could not load work item</Heading>
                <Text type="b200">
                    We could not load the requested work item. Please try
                    again.
                </Text>
            </Column>
        );
    } else if (loadStatus === WorkItemDetailStatus.FULFILLED && workItem) {
        // Fulfilled load — render the three sections and the Resolve button.
        content = (
            <Column spacing="500">
                <Box className="work-item-detail-section">
                    <ExceptionDetailsSection workItem={workItem} />
                </Box>
                <Box className="work-item-detail-section">
                    <AdditionalDetailsSection
                        additionalAttributes={workItem.additionalAttributes}
                    />
                </Box>
                <Box className="work-item-detail-section">
                    <SopSection />
                </Box>
                <Box className="work-item-detail-resolve">
                    <ResolveTaskButton workItemId={workItem.workItemId} />
                </Box>
            </Column>
        );
    } else {
        // Idle pre-effect state — render a neutral spacer so the page is
        // never empty while React schedules the mount effect.
        content = <Box data-testid="work-item-detail-idle" />;
    }

    return (
        <Box
            data-testid="work-item-detail-page"
            className="work-item-detail-page"
        >
            <Column spacing="500">{content}</Column>
        </Box>
    );
};

export default WorkItemDetail;

import './index.scss';